<template>
	<Drawer @requestUnmount="hide" :title="inUpdateMode ? $t('Edit user') : $t('Add user')" initialWidth="md">
		<template #default="{ hide }">

			<form @submit.prevent="save">
				<div class="pb-16 px-6">

					<div class="bg-base pt-4">
						<p v-if="usersStore.errors?.generic" class="text-red-600 text-xs">
							{{ usersStore.errors?.generic.toLocaleString($i18n) }}
						</p>

						<div class="grid grid-cols-6 gap-6 row">

							<!-- <div class="" v-if="!inUpdateMode">
								<sms-input type="name" :placeholder="$t('Name')" class="invalid:border-danger"
											 autofocus v-model="editableModel.name" :label="$t('Name')"
											 :validationError="usersStore.errors?.fields.name"></sms-input>
							</div> -->

							<!-- Email -->
							<div class="col-span-6 sm:col-span-6">
								<template v-if="inUpdateMode">
									<sms-input disabled :modelValue="editableModel.email" :label="$t('Email address')">
									</sms-input>
								</template>
								<template v-else>
									<sms-input type="email" autofocus
										:placeholder="$t('Email address') + '@example.com'"
										class="invalid:border-danger" v-model="editableModel.email"
										:label="$t('Email address')"
										:validationError="usersStore.errors?.fields.email?.toLocaleString($i18n)">
									</sms-input>
								</template>
							</div>

							<!-- Role -->
							<div class="col-span-6 sm:col-span-6">
								<sms-select :label="$t('Role')" :options="getAvailableRoles()"
									v-model="editableModel.role" :validationError="usersStore.errors?.fields.role?.toLocaleString($i18n)"
									:disabled="canEditRole() === false">
								</sms-select>
							</div>

							<!-- Status -->
							<div class="col-span-6 sm:col-span-6">
								<div v-if="inUpdateMode && !editableModel.isMe()" class="pl-1 flex items-center">
									<input type="checkbox" id="status" class="checkbox checkbox-accent"
										v-model="editableModel.active" />
									<label for="status" class="label-text pl-2 cursor-pointer">{{ $t('Active')
									}}</label>
								</div>
							</div>

							<!-- Invite -->
							<div class="col-span-6 sm:col-span-6">
								<div v-if="!inUpdateMode" class="pl-1 flex items-center">
									<input type="checkbox" checked id="send-invite" class="checkbox"
										v-model="sendInvitation" />
									<label for="send-invite" class="label-text pl-2 cursor-pointer">
										{{ $t('Send an invitation email') }}
									</label>
								</div>
							</div>

							<!-- Classrooms -->
							<div class="col-span-6 sm:col-span-6">
								<label class="label border-b pb-0 mb-2">
									<span class="label-text font-semibold">{{ $t("Has access to these classrooms") }}</span>
								</label>
								<div class="pl-2">
									<div v-if="classroomsStore.loading" class="py-2 text-center">
										<span class="loading loading-spinner"></span>
									</div>
									<div v-if="!classroomsStore.loading && !allClassrooms.length" class="text-base-content-light py-2 italic text-sm">
										{{ $t('No classrooms yet') }}
									</div>
									<template v-if="isAdminOrOwner">
										<div
											v-for="classroom in allClassrooms"
											:key="classroom.id"
											class="flex items-center gap-3 text-sm py-2"
										>
											<input
												type="checkbox"
												checked
												disabled
												class="checkbox checkbox-sm checkbox-primary border-none"
											/>
											<label class="hover:cursor-not-allowed">
												{{ classroom.name }}
											</label>
										</div>
									</template>
									<template v-else>
										<div
											v-for="classroom in allClassrooms"
											:key="classroom.id"
											class="flex items-center gap-3 text-sm py-2 rounded"
										>
											<input
												type="checkbox"
												:id="'classroom-' + classroom.id"
												:value="classroom.id"
												v-model="selectedClassroomIds"
												class="checkbox checkbox-sm checkbox-primary"
											/>
											<label :for="'classroom-' + classroom.id" class="hover:cursor-pointer">
												{{ classroom.name }}
											</label>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="drawer-action-bar" :class="[inUpdateMode ? 'justify-between' : 'justify-end']">
					<Dropdown v-if="inUpdateMode" position="top-start">
						<template #trigger>
							<button v-if="scope === 'organisation'" type="button" class="btn"
								:class="[saving ? 'btn-disabled' : 'btn-danger btn-outline']">
								<span v-show="deleting" class="loading loading-spinner"></span>
								<TrashIcon class="w-5 h-5" />
							</button>
						</template>
						<template #content="dialogActions">
							<DropdownDialogConfirm :confirm-label="$t('Yes')" :cancel-label="$t('No')"
								confirm-style="danger" class="w-80" @cancel="dialogActions.close"
								@confirm="confirmRemove(dialogActions)">
								<span class="text-danger">{{ $t('Are you sure?') }}</span>
							</DropdownDialogConfirm>
						</template>
					</Dropdown>

					<div class="flex justify-end gap-1">
						<button type="button" class="btn btn-ghost" @click="hide"
							:class="[saving || deleting ? 'hidden' : '']">
							{{ $t('Cancel') }}
						</button>
						<button type="submit" class="btn" :class="[deleting ? 'btn-disabled' : 'btn-primary']">
							<span v-show="saving" class="loading loading-spinner"></span>
							{{ $t('Save') }}
						</button>
					</div>
				</div>
			</form>
		</template>
	</Drawer>
</template>

<script lang="ts">
import { mapStores } from "pinia";

import { useUsersStore } from "@/stores/Users.store";
import { useClassroomsStore } from "@/stores/Classrooms.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";

import { User } from "@/models/User.model";

import Drawer from "@/components/ui/Drawer.v2.vue";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownDialogConfirm from "@/components/ui/DropdownDialogConfirm.vue";
import { TrashIcon } from "@heroicons/vue/24/outline";

export default {

	emits: ['requestClose'],

	props: {
		user: {
			type: User,
			required: false,
		},
		scope: {
			type: String,
			default: 'organisation',
		},
	},

	components: {
		Drawer,
		Dropdown,
		DropdownDialogConfirm,
		TrashIcon,
	},

	data() {
		return {
			saving: false,
			deleting: false,
			originalModel: null,
			editableModel: null,
			sendInvitation: true,
			selectedClassroomIds: [] as string[],
		}
	},

	computed: {
		...mapStores(useUsersStore, useClassroomsStore),

		inUpdateMode() {
			return (this.user && this.user.id) ? true : false;
		},

		isAdminOrOwner() {
			return this.editableModel?.role === 'admin' || this.editableModel?.role === 'owner';
		},

		allClassrooms() {
			return [...this.classroomsStore.items].sort((a, b) =>
				(a.name || '').localeCompare(b.name || '')
			);
		},
	},

	methods: {

		hide() {
			this.usersStore.clearErrors();
			this.$emit('requestClose');
		},

		async confirmRemove(dialogActions) {

			dialogActions.close();

			if (this.deleting) {
				return;
			}
			this.deleting = true;

			try {
				await this.usersStore.delete(this.originalModel);
				this.deleting = false;

				this.hide();

			} catch (e: any) {
				this.deleting = false;
				if (!(e instanceof ApiErrorMessageBag)) {
					throw e;
				}
			}
		},

		async save() {

			if (this.saving) {
				return;
			}
			this.saving = true;

			try {
				await this.usersStore.save(this.editableModel, this.sendInvitation);

				if (!this.isAdminOrOwner) {
					for (const classroom of this.classroomsStore.items) {
						const hasAccess = classroom.teachers && classroom.teachers.includes(this.editableModel.id);
						const shouldHaveAccess = this.selectedClassroomIds.includes(classroom.id);

						if (hasAccess !== shouldHaveAccess) {
							const classroomClone = classroom.clone();
							if (shouldHaveAccess) {
								classroomClone.teachers = [...(classroomClone.teachers || []), this.editableModel.id];
							} else {
								classroomClone.teachers = (classroomClone.teachers || []).filter(id => id !== this.editableModel.id);
							}
							await this.classroomsStore.save(classroomClone);
						}
					}
				}

				this.saving = false;
				this.hide();

			} catch (e) {
				// error message is populated now.
				this.saving = false;
				if (!(e instanceof ApiErrorMessageBag)) {
					throw e;
				}
			}
		},

		getAvailableRoles() {

			const roles = [];

			// We need to know our own role in order to determine which roles we can assign to other users
			const myRole = this.usersStore.me.role;
			switch (myRole) {
				case 'admin':
					roles.push('admin', 'teacher');
					break;

				case 'owner':

					if (this.editableModel.role === 'owner') {
						// owner cannot be demoted, unless there is another owner
						// Is this the last owner?
						if (this.usersStore.owners.length < 2) {
							roles.push('owner');
							break;
						}
					}

					roles.push('owner', 'admin', 'teacher');
					break;
			}

			if (this.editableModel.role === 'owner' && !roles.includes('owner')) {
				roles.unshift('owner');
			}

			return roles.map(role => ({
				label: this.$t(role[0].toUpperCase() + role.slice(1)),
				value: role,
			}));
		},

		canEditRole() {
			const myRole = this.usersStore.me.role;
			switch (myRole) {
				case 'admin':
					return this.editableModel.role !== 'owner';

				case 'owner':
					return true;
			}

			return false;
		}
	},

	async beforeMount() {
		if (this.user && this.user.id) {
			this.originalModel = this.user;
			// clone the store model to allow for manipulation without instantly affecting the store
			this.editableModel = this.user.clone();
		} else {
			this.editableModel = this.usersStore.new();
		}

		await this.classroomsStore.loadOnce();

		this.selectedClassroomIds = this.classroomsStore.items
			.filter(c => c.teachers && c.teachers.includes(this.user.id))
			.map(c => c.id);
	},
}
</script>
